/**
 * 支付结果页：失败订单 agent-tip 数据与埋点
 */

import { resolvePayFailTipConfig } from '@configs/officialTopupNew/payFailTip';
import { getHrefParamFromLocation } from '@utiles/hrefParam';
import { trackPayAssistantPoint } from './analytics';
import { getEntryTexts, safeOpenAiPanel } from './instance';
import {
  fetchPaymentAssistantOrders,
  pickPaymentAssistantOrder,
  pollPaymentAssistantOrder,
  resolvePaymentAssistantOrderId,
} from './getOrders';
import {
  consumePayFailSwitchPaytypeResume,
  isPayFailSwitchPaytypeEntry,
  redirectToPayHomeForSwitchPaytype,
} from './payFailResume';

const PAY_FAIL_TIP_CLICK_ACTION_MAP = {
  contact_service: 'fail_btn_contact_service',
  switch_paytype: 'fail_btn_switch',
};

/**
 * @param {Record<string, unknown>} [order]
 */
function buildPayFailOrderTrackFields(order = {}) {
  return {
    fPayType: order?.f_pay_type || '',
    payType: order?.pay_type || '',
    amount: order?.local_money || '',
    currency: order?.local_currency || '',
    failedCode: order?.failed_code || '',
    failedReason: order?.failed_reason || '',
  };
}

/**
 * @param {string} buttonAction
 * @returns {string}
 */
function resolvePayFailTipClickAction(buttonAction) {
  return PAY_FAIL_TIP_CLICK_ACTION_MAP[buttonAction] || 'pay_fail_tip_click';
}

/**
 * @param {Record<string, unknown>} order
 * @returns {null | { order: Record<string, unknown>, tip: ReturnType<typeof resolvePayFailTipConfig> }}
 */
export function buildPayFailTipPayload(order) {
  if (!order || Number(order.result) !== -1) {
    return null;
  }
  const tip = resolvePayFailTipConfig(order.failed_type, getEntryTexts());
  return { order, tip };
}

/**
 * 拉取订单并在 result === -1 时返回 tip 配置
 * @param {{ gameId: string|number, orderId?: string }} options
 * @returns {Promise<null | {
 *   order: Record<string, unknown>,
 *   tip: ReturnType<typeof resolvePayFailTipConfig>,
 * }>}
 */
export async function loadPayFailTipForOrder(options = {}) {
  const { gameId, orderId } = options;
  if (!gameId) return null;

  const list = await fetchPaymentAssistantOrders({ gameId, orderId });
  const order = pickPaymentAssistantOrder(list, orderId);
  return buildPayFailTipPayload(order);
}

/**
 * URL result=0 时轮询至订单终态；仅 result=-1 返回 tip 配置
 * 购物车订单：isCart=true 时 query orderId 会在末尾拼「1」再查 get_orders
 * @param {{
 *   gameId: string|number,
 *   orderId?: string,
 *   isCart?: boolean,
 *   shouldContinue?: () => boolean,
 * }} options
 */
export async function pollPayFailTipForOrder(options = {}) {
  const { gameId, orderId, isCart, ...rest } = options;
  const queryOrderId = resolvePaymentAssistantOrderId(orderId, isCart);
  const order = await pollPaymentAssistantOrder({
    gameId,
    orderId: queryOrderId,
    ...rest,
  });
  return buildPayFailTipPayload(order);
}

/**
 * 进入支付结果页且页面结果为失败时上报
 * @param {Record<string, unknown>} [context]
 */
export function trackPayFailDetected(context = {}) {
  trackPayAssistantPoint({
    action: 'pay_fail_detected',
    category: 'tip',
    actor: 'host',
    tipKind: 'pay_fail',
    gameId: context.gameId || '',
    orderId: context.orderId || '',
    fPayType: context.fPayType || '',
    payType: context.payType || '',
    failMsg: context.msg || '',
    isCart: !!context.isCart,
    productId: context.productId || '',
    cpProductId: context.cpProductId || '',
  });
}

/**
 * @param {ReturnType<typeof resolvePayFailTipConfig>} tip
 * @param {Record<string, unknown>} [order]
 */
export function trackPayFailTipShow(tip, order = {}) {
  const trackFields = {
    failType: tip?.failType || '',
    orderId: order?.order_id || '',
    ...buildPayFailOrderTrackFields(order),
  };
  trackPayAssistantPoint({
    action: tip.trackEvent,
    category: 'tip',
    actor: 'host',
    tipKind: 'pay_fail',
    ...trackFields,
  });
  trackPayAssistantPoint({
    action: 'fail_bubble_show',
    category: 'tip',
    actor: 'host',
    tipKind: 'pay_fail',
    failTrackEvent: tip?.trackEvent || '',
    ...trackFields,
  });
}

/** 同页仅上报一次 channel_switch_success */
let channelSwitchSuccessReported = false;

/**
 * 结果页切换渠道后，相同商品/购物车重新拉起收银台时上报
 * @param {Record<string, unknown>} [context]
 */
export function trackChannelSwitchSuccess(context = {}) {
  if (channelSwitchSuccessReported) return;
  channelSwitchSuccessReported = true;
  trackPayAssistantPoint({
    action: 'channel_switch_success',
    category: 'tip',
    actor: 'host',
    tipKind: 'pay_fail',
    payResume: context.payResume || '',
    gameId: context.gameId || '',
    orderId: context.orderId || '',
    productId: context.productId || '',
    cpProductId: context.cpProductId || '',
    isCart: !!context.isCart,
  });
}

/**
 * Home 收银台拉起成功后：结果页「切换支付方式」回跳链路打点（仅一次）
 * @param {Record<string, unknown>} [context]
 */
export function maybeTrackPayFailChannelSwitchSuccess(context = {}) {
  if (!isPayFailSwitchPaytypeEntry()) return;
  trackChannelSwitchSuccess({
    payResume: getHrefParamFromLocation('payResume') || '',
    ...context,
  });
  consumePayFailSwitchPaytypeResume();
}

/**
 * @param {'primary' | 'secondary'} slot
 * @param {ReturnType<typeof resolvePayFailTipConfig>} tip
 * @param {Record<string, unknown>} [order]
 */
export function trackPayFailTipClick(slot, tip, order = {}) {
  const btn = slot === 'primary' ? tip.primary : tip.secondary;
  if (!btn) return;
  const buttonAction = btn?.action || '';
  trackPayAssistantPoint({
    action: resolvePayFailTipClickAction(buttonAction),
    category: 'tip',
    actor: 'host',
    tipKind: 'pay_fail',
    failType: tip?.failType || '',
    buttonAction,
    buttonSlot: slot || '',
    orderId: order?.order_id || '',
    ...buildPayFailOrderTrackFields(order),
  });
}

/**
 * @param {import('@configs/officialTopupNew/payFailTip').PayFailTipAction} action
 * @param {import('./payFailResume').PayFailHomeContext} [resumeContext]
 * @returns {Promise<void>}
 */
export async function handlePayFailTipAction(action, resumeContext) {
  if (action === 'contact_service') {
    // 【兜底】打开联系客服技能失败不影响失败 tip 其它按钮（切渠道/重试）
    await safeOpenAiPanel({
      inputValue: '',
      extensions: { skill: 'contact_service' },
    });
    return;
  }

  if (action === 'switch_paytype') {
    redirectToPayHomeForSwitchPaytype(resumeContext || {});
  }
}
